import { getChannel, getChannels, getChannelTags } from 'api/socket.request'
import { useCategoryUnreadCount } from 'contexts/CategoryUnreadCountProvider'
import { useTalkPlusClient } from 'contexts/TalkPlusClientContext'
import dayjs from 'dayjs'
import { ChatStatus, CustomChannelInfo } from 'interfaces/talkplus.interface'
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { InfiniteData, useInfiniteQuery, useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'
import { Channel, EventData, EventTypeEnum, Member } from 'talkplus-sdk'
import { CHAT_BLOCK_GUEST_IDS } from 'config/const'
import { ITag } from 'interfaces/tag.interface'
import { STORAGE_KEY } from 'config/const'
import { useTags } from 'contexts/MultipleTagProvider'

type ChannelListData = InfiniteData<{
    hasNext: boolean
    channels: CustomChannelInfo[]
    cursor?: string
}> | null

// Context 타입 정의
interface ChannelListContextType {
    displayChannelData: ChannelListData | null
    fetchNextPage: () => void
    hasNextPage: boolean | undefined
    isLoading: boolean
    isFetchingNextPage: boolean
    handleFilterStatus: (status: ChatStatus) => void
    filterStatus: ChatStatus
    channelListQueryKey: (string | number | string[] | undefined)[]
    scrollContainerRef: React.RefObject<HTMLDivElement>
    appliedFilterTag: ITag[] | null
    handleAppliedFilterTag: (filterTags: ITag[]) => void
    isEditMode: boolean
    handleEditMode: (isEditMode: boolean) => void
    selectedEditTag: ITag | null
    handleSelectedEditTag: (tag: ITag) => void
    selectedEditChannelList: CustomChannelInfo[]
    handleSelectedEditChannels: (channels: CustomChannelInfo[]) => void
    displayChannelDataForceUpdate: (newDisplayChannelData: ChannelListData) => void
    resetChannelListQuery: () => void
}

// Context 생성
export const ChannelListContext = createContext<ChannelListContextType | null>(null)

// Provider 컴포넌트
const ChannelListProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const { client, isTalkplusLoginComplete } = useTalkPlusClient()
    const queryClient = useQueryClient()
    const user = useRecoilValue(userState)
    const [filterStatus, setFilterStatus] = useState<ChatStatus>(ChatStatus.PENDING)
    const [displayChannelData, setDisplayChannelData] = useState<ChannelListData>(null)
    const { debouncedFetchUnreadCounts } = useCategoryUnreadCount()
    const [isPageFocus, setIsPageFocus] = useState(true)
    const [appliedFilterTag, setAppliedFilterTag] = useState<ITag[] | null>(null)
    const [selectedEditTag, setSelectedEditTag] = useState<ITag | null>(null)
    const [selectedEditChannelList, setSelectedEditChannelList] = useState<CustomChannelInfo[]>([])
    const { allTags } = useTags()
    const [isEditMode, setIsEditMode] = useState(false)
    const location = useLocation()
    const pathname = location.pathname
    const scrollContainerRef = useRef<HTMLDivElement>(null) // 내부 참조

    // 필터 태그 아이디 배열 - 채널 리스트 쿼리키에 넣기 위해 사용 ( 채널 리스트 필터링 )
    const filterTagIds = useMemo(
        () => appliedFilterTag?.map((tag) => tag.id) || [],
        [appliedFilterTag]
    )

    // 채널 상태 필터 변경 핸들러
    const handleFilterStatus = (status: ChatStatus) => {
        setFilterStatus(status)
        setDisplayChannelData(null)
    }

    // 태그 필터 변경 핸들러
    const handleAppliedFilterTag = (filterTags: ITag[]) => {
        setAppliedFilterTag(filterTags)
    }

    // 문의톡 리스트 필터 수정모드 여부 변경 핸들러
    const handleEditMode = (isEditMode: boolean) => {
        if (!isEditMode) {
            // 편집모드를 종료할 때는 선택된 채널 리스트 초기화
            setSelectedEditChannelList([])
            // 편집모드 종료 시 선택된 태그 정보 초기화
            setSelectedEditTag(null)
            // 편집모드 종료 시 편집모드 여부 변경
            setIsEditMode(false)
            // 편집모드 동안 데이터가 멈춰있었기에 편집모드 종료 시 채널 리스트 캐시 초기화
            channelListRefetch()
        } else {
            // 편집모드 시작 시 편집모드 여부 변경
            setIsEditMode(true)
        }
    }

    // 문의톡 리스트 필터 수정모드 태그 변경 핸들러
    const handleSelectedEditTag = (tag: ITag) => {
        setSelectedEditTag(tag)
    }

    // 문의톡 리스트 필터 수정모드 채널 선택 핸들러
    const handleSelectedEditChannels = useCallback((channels: CustomChannelInfo[]) => {
        setSelectedEditChannelList(channels)
    }, [])

    /**
     * 채널 목록 데이터 불러오기
     * - 서버로부터 채널 데이터를 페이징으로 요청
     */
    const fetchChannels = async ({ pageParam }: { pageParam: string | null }) => {
        if (!isTalkplusLoginComplete || pageParam === null) {
            return { hasNext: false, channels: [] }
        }
        const size = 100
        const res = await getChannels({
            size,
            category: filterStatus,
            next: pageParam,
            tagIds: filterTagIds,
        })

        if (!res.data?.data) throw new Error('채널 데이터를 불러오는 중 오류 발생')

        const customChannels = res.data.data.items.map(customChannelList)

        const blockedUserFilteredItems = customChannels.filter((item) => {
            if (item.guestId) {
                return !CHAT_BLOCK_GUEST_IDS.includes(item.guestId)
            }
            return true
        })

        return {
            hasNext: Boolean(res.data.data.next),
            channels: blockedUserFilteredItems,
            cursor:
                filterStatus === ChatStatus.COMPLETED
                    ? customChannels.at(-1)?.rawUpdatedAt
                    : customChannels.at(-1)?.rawSentAt,
        }
    }

    const channelListQueryKey = useMemo(
        () => ['channel-list', user?.userId, filterStatus, filterTagIds],
        [user, filterStatus, filterTagIds]
    )

    // React Query: Infinite Query를 활용한 데이터 페이징
    const {
        data: baseChannelData,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetchingNextPage,
    } = useInfiniteQuery(
        channelListQueryKey,
        ({ pageParam = '' }) => fetchChannels({ pageParam }),
        {
            getNextPageParam: (lastPage) => {
                return lastPage?.hasNext ? lastPage.cursor : null
            },
            // 초기 데이터 로드 시 필터 태그가 없으면 데이터 로드 안되도록 처리
            enabled: isTalkplusLoginComplete && appliedFilterTag !== null,
        }
    )

    // 최초 렌더링 시 태그 정보를 로컬 저장소에서 가져온 후 필터 태그 정보가 없으면 빈 배열로 초기화
    // 해당 업데이트를 해주어야 useQuery 조건에서 필터 태그를 설정하여 enabled 조건에서 필터 태그가 없으면 데이터 로드 안되도록 처리할 수 있음
    useEffect(() => {
        if (user?.userId && allTags) {
            try {
                const filterTag = localStorage.getItem(STORAGE_KEY.filterTag(user.userId))
                if (filterTag) {
                    const parseSavedFilterTag = JSON.parse(filterTag) // 로컬 저장소에 저장된 태그 정보를 파싱
                    const activeTags = parseSavedFilterTag.filter(
                        (
                            tag: ITag // 로컬 저장소에 저장되었던 태그 중 현재 활성화된 태그만 추려냄
                        ) => allTags.some((t) => t.id === tag.id)
                    )
                    // 현재 활성화된 태그 정보를 로컬 저장소에 저장
                    localStorage.setItem(
                        STORAGE_KEY.filterTag(user.userId),
                        JSON.stringify(activeTags)
                    )
                    setAppliedFilterTag(activeTags)
                } else {
                    setAppliedFilterTag([])
                }
            } catch (err) {
                setAppliedFilterTag([])
                localStorage.removeItem(STORAGE_KEY.filterTag(user.userId))
            }
        }
    }, [user, allTags])

    /**
     * 채널의 태그 정보 가져오기
     * - 기존 채널에서 태그 정보를 가져오거나 서버에서 조회
     */
    const updateChannelTagInfo = useCallback(
        async (updateChannel: CustomChannelInfo, channelId: string): Promise<CustomChannelInfo> => {
            // 메시지가 수신된 채널이 기존 데이터에 존재하는지 확인
            const flatOldChannels = baseChannelData?.pages.flatMap((page) => page.channels) || []

            // 기존 채널 중 메시지가 수신된 채널의 태그 정보 찾기
            const prevUpdateChannel = flatOldChannels.find(
                (channel: CustomChannelInfo) => channel.chatRoomId === channelId
            )

            // 기존 채널에 태그 정보가 있으면 그대로 사용
            if (prevUpdateChannel?.tags) {
                updateChannel.tags = prevUpdateChannel.tags
                return updateChannel
            }

            // 기존 채널이 없거나 태그 정보가 없으면 서버에서 정보 가져오기
            try {
                // 클릭메이트 서버에서 채널 정보 조회
                const channelInfoRes = await getChannel(channelId)
                const channelInfo = channelInfoRes.data?.data

                // 채널 idx가 있으면 태그 정보도 함께 조회
                if (channelInfo?.idx) {
                    updateChannel.idx = channelInfo.idx
                    const channelTagsRes = await getChannelTags(channelInfo.idx)
                    const channelTags = channelTagsRes.data?.data

                    // 태그 정보가 있으면 업데이트 채널에 태그 정보 추가
                    if (channelTags) {
                        updateChannel.tags = channelTags
                    }
                }
            } catch (err) {
                console.log('err', err)
            }

            return updateChannel
        },
        [baseChannelData]
    )

    /**
     * 채널 추가
     * - 새 채널 데이터를 기존 목록에 추가하고 클라이언트 데이터 업데이트
     */
    type ChannelPage = { hasNext: boolean; channels: CustomChannelInfo[]; cursor?: string }
    type ChannelData = InfiniteData<ChannelPage>
    const addChannel = useCallback(
        async (updateChannel: CustomChannelInfo, channelId: string) => {
            if (!baseChannelData || !baseChannelData.pages || baseChannelData.pages.length === 0) {
                // 기존 채널 정보가 없다면 얼리 리턴
                return
            }

            // 채널 태그 정보 가져오기
            const updatedChannel = await updateChannelTagInfo(updateChannel, channelId)

            if (appliedFilterTag && appliedFilterTag.length > 0) {
                // 필터가 적용되어 있다면, 문의톡이 수신된 채널에 해당 태그가 존재하는지 확인
                const isTagFilterMatch = updateChannel.tags?.some((tag) =>
                    appliedFilterTag.some((filterTag) => filterTag.id === tag.id)
                )

                // 만약 필터가 적용되어있고 일치하는 태그가 없다면 채널 리스트에 추가하지 않음
                if (!isTagFilterMatch) {
                    return
                }
            }

            // 서버에 요청하지 않고 클라이언트 데이터 수정
            queryClient.setQueryData<ChannelData>(channelListQueryKey, (oldData) => {
                if (!oldData || !oldData.pages || oldData.pages.length === 0) return oldData as any

                // 첫 페이지에 새 채널 추가 및 중복 제거
                const firstPage = {
                    ...oldData.pages[0],
                    channels: [
                        updatedChannel,
                        ...(oldData.pages[0].channels || []).filter(
                            (channel: CustomChannelInfo) => channel.chatRoomId !== channelId
                        ),
                    ],
                }

                // 나머지 페이지에서 중복 채널 제거
                const remainingPages = oldData.pages.slice(1).map((page) => ({
                    ...page,
                    channels: (page.channels || []).filter(
                        (channel: CustomChannelInfo) => channel.chatRoomId !== channelId
                    ),
                }))

                return {
                    ...oldData,
                    pages: [firstPage, ...remainingPages],
                } as ChannelData
            })
        },
        [queryClient, channelListQueryKey, baseChannelData, updateChannelTagInfo, appliedFilterTag]
    )

    useEffect(() => {
        const handleFocus = () => {
            setIsPageFocus(true)
        }

        const handleBlur = () => {
            setIsPageFocus(false)
        }

        window.addEventListener('focus', handleFocus)
        window.addEventListener('blur', handleBlur)

        return () => {
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('blur', handleBlur)
        }
    }, [])

    useEffect(() => {
        if (isPageFocus) {
            debouncedFetchUnreadCounts()
        }
        if (isPageFocus && pathname === '/chat' && !isEditMode) {
            // 채널 리스트 페이지일 경우에만 focus 여부가 변경될 때마다 채널 리스트 refetch
            // 이유 - 게스트 패널 > 태그 변경 > markAsRead 호출하여 channelChanged 이벤트로 채널 리스트 태그 업데이트 하려했으나 대기 채널은 markAsRead 호출 안됨
            channelListRefetch()
        }
    }, [isPageFocus])

    const channelListRefetch = useCallback(async () => {
        await queryClient.refetchQueries(channelListQueryKey)
    }, [channelListQueryKey, queryClient])

    const handleEvent = useCallback(
        async (data: EventData) => {
            // 메시지 이벤트 처리
            const isMessageEvent = data.type === EventTypeEnum.Message
            const isChannelChangedEvent = data.type === EventTypeEnum.ChannelChanged
            const isMatchingCategory = data.channel?.category === filterStatus
            const isChannelListPage = pathname === '/chat'

            // 채널 리스트 페이지에서만 메세지 이벤트 처리
            // 편집모드일떄는 수신된 메세지 처리하지 않음
            if (
                isMessageEvent &&
                isMatchingCategory &&
                filterStatus !== ChatStatus.COMPLETED &&
                data.channel &&
                isChannelListPage &&
                !isEditMode
            ) {
                addChannel(customChannelList(data.channel), data.channel.id)
            }

            if (isChannelChangedEvent && isChannelListPage && !isEditMode) {
                channelListRefetch()
            }
        },

        [filterStatus, pathname, addChannel, channelListRefetch, isEditMode]
    )

    useEffect(() => {
        client.on('event', handleEvent)

        return () => {
            client.off('event', handleEvent) // 여기서 반환 값이 없음
        }
    }, [client, handleEvent])

    useEffect(() => {
        // user가 바뀔경우 filterStatus를 초기화
        setFilterStatus(ChatStatus.PENDING)
    }, [user])

    const isSameChannel = useCallback(
        (channel1: CustomChannelInfo, channel2: CustomChannelInfo) => {
            // 기존 채널이 없을경우 무조건 업데이트
            if (channel2 === undefined) return false
            return JSON.stringify(channel1) === JSON.stringify(channel2)
        },
        []
    )

    useEffect(() => {
        if (isEditMode) {
            // 편집모드일 경우 화면에 보이는 채널 리스트 업데이트 안함
            return
        }

        const copyBaseChannelData = structuredClone(baseChannelData)

        const restoredScrollPosition = () => {
            // 데이터가 바뀌면 스크롤 위치 설정 및 복원 setTimeout 사용
            let scrollY = scrollContainerRef.current?.scrollTop || 0
            setTimeout(() => {
                if (scrollY > 0) {
                    scrollContainerRef.current?.scrollTo(0, scrollY)
                }
            }, 10)
        }

        // baseChannelData가 없을경우 displayChannelData를 초기화
        if (
            copyBaseChannelData &&
            copyBaseChannelData.pages.length > 0 &&
            copyBaseChannelData.pages[0].channels.length === 0
        ) {
            // console.log('baseChannelData가 없어서 displayChannelData를 초기화 합니다.')
            setDisplayChannelData(null)
            restoredScrollPosition()
            return
        }

        // displayChannelData가 없으면서 baseChannelData가 있으면 업데이트
        if (
            copyBaseChannelData &&
            copyBaseChannelData.pages.length > 0 &&
            copyBaseChannelData.pages[0].channels.length > 0 &&
            displayChannelData === null
        ) {
            // console.log('baseChannelData가 있지만 displayChannelData가 없어서 즉시 모든 데이터를 업데이트 합니다.')
            setDisplayChannelData(copyBaseChannelData)
            restoredScrollPosition()
            return
        }

        if (copyBaseChannelData) {
            const uniqueChatRoomIdMap = new Map()
            let shouldUpdate = false
            let logMessages: any[] = []

            let newDisplayChannelData = {
                pageParams: copyBaseChannelData.pageParams,
                pages: copyBaseChannelData.pages.map((page, pageIdx) => {
                    // 중복된 채팅방 ID를 제거한 고유한 채널 배열 생성
                    const uniqueChannels = page.channels.filter((currentChannel) => {
                        if (uniqueChatRoomIdMap.has(currentChannel.chatRoomId)) {
                            return false // 이미 존재하는 채팅방은 제외
                        }
                        uniqueChatRoomIdMap.set(currentChannel.chatRoomId, currentChannel)
                        return true // 새로운 채팅방만 포함
                    })

                    // 고유한 채널들을 순회하며 업데이트가 필요한지 확인
                    const updatedChannels = uniqueChannels.map((channel, channelIdx, array) => {
                        // 현재 표시 중인 채널 데이터 가져오기
                        const displayChannel =
                            displayChannelData?.pages[pageIdx]?.channels[channelIdx]

                        if (
                            displayChannel &&
                            array.length < displayChannelData?.pages[pageIdx].channels.length
                        ) {
                            logMessages.push(
                                '데이터의 길이가 달라져야 하기에 업데이트 플래그 설정',
                                array,
                                displayChannelData?.pages[pageIdx].channels
                            )
                            shouldUpdate = true
                            displayChannelData?.pages[pageIdx].channels.splice(channelIdx)
                        }

                        if (displayChannel) {
                            const isSame = isSameChannel(channel, displayChannel)

                            if (!isSame) {
                                logMessages.push('두 개의 채널이 달라요')
                                shouldUpdate = true
                            }
                        } else {
                            logMessages.push('채널 정보가 없어서 업데이트 해야해요')
                            shouldUpdate = true
                        }

                        return channel
                    })

                    // 페이지 업데이트
                    page.channels = updatedChannels

                    return page
                }),
            }

            if (shouldUpdate) {
                // console.log(logMessages.join('\n')) // 모든 로그 메시지를 한 번에 출력
                setDisplayChannelData(newDisplayChannelData)

                restoredScrollPosition()
            }
        }
    }, [baseChannelData, displayChannelData, isSameChannel, isEditMode])

    const resetChannelListQuery = useCallback(() => {
        // 필터링 상태 변경 시 채널 리스트 캐시 초기화

        queryClient.resetQueries(channelListQueryKey)
        setDisplayChannelData(null)
        if (scrollContainerRef?.current) {
            scrollContainerRef.current.scrollTo({
                top: 0, // 맨 위로 이동
            })
        }
    }, [queryClient, channelListQueryKey])

    // 화면에 보이는 문의톡 목록 데이터를 강제 업데이트하는 함수
    // 편집모드에서 데이터를 수정해야할 경우 사용 - 사용시 주의해야 함
    function displayChannelDataForceUpdate(newDisplayChannelData: ChannelListData) {
        setDisplayChannelData(newDisplayChannelData)
    }

    // 편집모드일경우 화면에 보여지는 데이터가 변경된다면, 이미 선택된 채널 리스트에서 화면에 보이지 않는 데이터는 제거
    useEffect(() => {
        // 편집모드가 아니거나 화면에 보여지는 데이터가 없으면 실행하지 않음
        if (!isEditMode || !displayChannelData) {
            return
        }

        // 화면에 보여지는 채널 ID Set 생성
        const displayChannelIds = new Set(
            displayChannelData.pages.flatMap((page) =>
                page.channels.map((channel) => channel.chatRoomId)
            ) || []
        )

        // 선택된 채널 중 화면에 보이는 채널만 필터링
        const visibleSelectedChannels = selectedEditChannelList.filter((channel) =>
            displayChannelIds.has(channel.chatRoomId)
        )

        // 선택된 채널이 변경된 경우에만 업데이트
        if (visibleSelectedChannels.length !== selectedEditChannelList.length) {
            handleSelectedEditChannels(visibleSelectedChannels)
        }
    }, [isEditMode, displayChannelData, selectedEditChannelList, handleSelectedEditChannels])

    return (
        <ChannelListContext.Provider
            value={{
                displayChannelData,
                fetchNextPage,
                hasNextPage,
                isLoading,
                isFetchingNextPage,
                handleFilterStatus,
                filterStatus,
                channelListQueryKey,
                scrollContainerRef,
                appliedFilterTag,
                handleAppliedFilterTag,
                isEditMode,
                handleEditMode,
                selectedEditTag,
                handleSelectedEditTag,
                selectedEditChannelList,
                handleSelectedEditChannels,
                displayChannelDataForceUpdate,
                resetChannelListQuery,
            }}
        >
            {children}
        </ChannelListContext.Provider>
    )
}

// Context 소비를 위한 커스텀 훅
export const useChannelList = (): ChannelListContextType => {
    const context = useContext(ChannelListContext)
    if (!context) {
        throw new Error('ChannelListProvider 내에서만 useChannelList를 사용할 수 있습니다.')
    }
    return context
}

export default ChannelListProvider

/**
 * 전송 시간 포맷팅 함수
 * - 날짜를 한국어 형식으로 변환
 *
 * [날짜 표기 방법 변경에 따른 코드 변경]
 * @author : yaro
 * @date : 2025.01.17
 */
const formatSentAt = (sentAt: string): string => {
    const now = dayjs()
    const date = dayjs(sentAt)

    if (!date.isSame(now, 'year')) {
        // 1. 현재 연도와 다를 경우
        return date.format('YY.M.D (ddd)')
    } else if (!date.isSame(now, 'day')) {
        // 2. 현재 연도와 동일하고 현재 일자와 다를 경우
        return date.format('M.D (ddd)')
    } else {
        // 3. 현재 연도와 동일하고 현재 일자가 같은 경우
        const hour = date.hour()
        const minute = date.minute()
        const period = hour < 12 ? '오전' : '오후'
        const hour12 = hour % 12 || 12
        return `${period} ${hour12}:${String(minute).padStart(2, '0')}`
    }

    // if (date.isSame(now, "day")) {
    //   const hour = date.hour();
    //   const minute = date.minute();
    //   const period = hour < 12 ? "오전" : "오후";
    //   const hour12 = hour % 12 || 12;
    //   return `${period} ${hour12}:${String(minute).padStart(2, "0")}`;
    // } else if (date.isSame(now.subtract(1, "day"), "day")) {
    //   return "어제";
    // } else if (date.isSame(now, "year")) {
    //   return date.format("M.DD (ddd)");
    // } else {
    //   return date.format("YYYY.M.DD (ddd)");
    // }
}

/**
 * 채널 데이터를 커스텀 형식으로 변환
 * - 서버에서 받은 채널 데이터를 UI에 필요한 형식으로 가공
 */
const customChannelList = (channel: CustomChannelInfo | Channel): CustomChannelInfo => {
    if ('id' in channel) {
        // talkplus에서 주는 채널 정보일 경우 customChannelInfo 형식으로 변환
        const KSTSentAt = dayjs(channel.lastSentAt).tz('Asia/Seoul').format('YYYY-MM-DD HH:mm:ss')
        const guest = channel.members?.find((member) => member.data?.role === 'GUEST') as Member
        const seller = channel.members?.find((member) => member.data?.role === 'SELLER') as Member

        return {
            category: channel.category as ChatStatus,
            // 게스트의 탈퇴 여부를 정확히 확인하려면 메세지가 올 때마다 서버에서 게스트의 탈퇴 여부를 받아와야 하는데 이는 서버의 부하를 발생시킬 우려가 있음
            // 메세지가 왔다는건 게스트가 메세지를 보낸것이므로 게스트가 탈퇴한 상황이 아니라고 가정해 우선 false로 설정
            isWithdrawnGuest: false,
            chatRoomId: channel.id,
            content: channel.lastMessage?.data?.imageURL
                ? '이미지'
                : channel.lastMessage?.text || '',
            guestId: Number(guest?.id || 0),
            guestName: guest?.username || '알 수 없음',
            guestProfileUrl: guest?.profileImageUrl || '',
            rawSentAt: String(channel.lastSentAt),
            rawUpdatedAt: String(channel.updatedAt),
            sellerId: Number(seller?.id || 0),
            sellerName: seller?.username || '알 수 없음',
            sellerProfileUrl: seller?.profileImageUrl || '',
            sentAt: KSTSentAt,
            unreadCount: channel.unreadCount || 0,
            formattedSentAt: formatSentAt(KSTSentAt),
            // 톡플러스에는 태그 정보가 없기에 빈 배열 리턴
            tags: [],
            // 톡플러스에는 idx 정보가 없기에 null 리턴
            idx: null,
        }
    }

    const timestampInSeconds = Number(channel.rawSentAt) / 1000
    const KSTSentAt = dayjs(timestampInSeconds * 1000)
        .tz('Asia/Seoul')
        .format('YYYY-MM-DD HH:mm:ss')

    return {
        ...channel,
        sentAt: KSTSentAt,
        formattedSentAt: formatSentAt(KSTSentAt),
    }
}
